import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  Atendente,
  CriarAtendenteRequest,
  CriarAtendenteResponse,
  AtualizarAtendenteRequest,
  AtualizarAtendenteResponse,
  AtualizarStatusRequest,
  AtualizarStatusResponse,
  ListarAtendentesResponse,
} from '../models/atendente.model';
import { ToastService } from 'src/app/shared/toasts/toast-service';
import { catchError, tap } from 'rxjs/operators';
import { throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AtendenteService {
  private apiUrl = `${environment.apiUrl}/v1/WhatsAppAtendentes`;

  // BehaviorSubject para gerenciar a lista de atendentes
  private atendentesSubject = new BehaviorSubject<Atendente[]>([]);
  atendentes$ = this.atendentesSubject.asObservable();

  constructor(
    private http: HttpClient,
    private toastService: ToastService
  ) {}

  criarAtendente(request: CriarAtendenteRequest): Observable<CriarAtendenteResponse> {
    const body = {
      usuarioId: request.usuarioId
    };
    return this.http.post<CriarAtendenteResponse>(this.apiUrl, body).pipe(
      tap(() => {
        // Atualizar a lista após criar
        this.atualizarLista();
      }),
      catchError((error) => {
        this.toastService.show('Erro ao criar atendente', {
          classname: 'bg-danger text-light',
        });
        return throwError(() => error);
      })
    );
  }

  atualizarAtendente(atendenteId: string, request: AtualizarAtendenteRequest): Observable<AtualizarAtendenteResponse> {
    const body = {
      usuarioId: request.usuarioId
    };
    return this.http.put<AtualizarAtendenteResponse>(`${this.apiUrl}/${atendenteId}`, body).pipe(
      tap(() => {
        // Atualizar a lista após atualizar
        this.atualizarLista();
      }),
      catchError((error) => {
        this.toastService.show('Erro ao atualizar atendente', {
          classname: 'bg-danger text-light',
        });
        return throwError(() => error);
      })
    );
  }

  listarAtendentes(status?: string): Observable<ListarAtendentesResponse> {
    let params = new HttpParams();
    if (status) {
      params = params.set('status', status);
    }

    return this.http.get<ListarAtendentesResponse>(this.apiUrl, { params }).pipe(
      tap((response) => {
        // Atualizar o BehaviorSubject com os dados recebidos
        this.atendentesSubject.next(response.atendentes || []);
      }),
      catchError((error) => {
        this.toastService.show('Erro ao listar atendentes', {
          classname: 'bg-danger text-light',
        });
        return throwError(() => error);
      })
    );
  }

  /**
   * Atualiza a lista de atendentes e emite no BehaviorSubject
   */
  atualizarLista(status?: string): void {
    this.listarAtendentes(status).subscribe();
  }

  atualizarStatus(
    atendenteId: string,
    status: 'online' | 'ausente' | 'offline'
  ): Observable<AtualizarStatusResponse> {
    const request: AtualizarStatusRequest = {
      status: status
    };
    return this.http
      .put<AtualizarStatusResponse>(`${this.apiUrl}/${atendenteId}/status`, request)
      .pipe(
        tap(() => {
          // Atualizar a lista após atualizar status
          this.atualizarLista();
        }),
        catchError((error) => {
          this.toastService.show('Erro ao atualizar status do atendente', {
            classname: 'bg-danger text-light',
          });
          return throwError(() => error);
        })
      );
  }

  obterAtendentePorUsuarioId(usuarioId: string): Observable<Atendente> {
    return this.http
      .get<Atendente>(`${this.apiUrl}/por-usuario/${usuarioId}`)
      .pipe(
        catchError((error) => {
          // Não mostrar erro se não encontrar (atendente pode não existir ainda)
          if (error.status !== 404) {
            this.toastService.show('Erro ao buscar atendente', {
              classname: 'bg-danger text-light',
            });
          }
          return throwError(() => error);
        })
      );
  }

  removerAtendente(atendenteId: string): Observable<{ success: boolean; data: { mensagem: string } }> {
    return this.http
      .delete<{ success: boolean; data: { mensagem: string } }>(`${this.apiUrl}/${atendenteId}`)
      .pipe(
        tap(() => {
          // Atualizar a lista após remover
          this.atualizarLista();
        }),
        catchError((error) => {
          this.toastService.show('Erro ao remover atendente', {
            classname: 'bg-danger text-light',
          });
          return throwError(() => error);
        })
      );
  }
}
